import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../contexts/AuthContext';
import lavenderIconLight from '../../assets/blue_unfilled_icon_light.png';
import lavenderIconDark from '../../assets/lavender_unfilled_icon_dark.png';

const API_URL = '/api';

function Login() {
  const [isLogin, setIsLogin] = useState(window.location.hash !== '#register');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [expandedTerms, setExpandedTerms] = useState(false);
  const [expandedPrivacy, setExpandedPrivacy] = useState(false);
  const [termsContent, setTermsContent] = useState('Loading...');
  const [privacyContent, setPrivacyContent] = useState('Loading...');
  const { login, darkMode } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch Terms of Service and Privacy Policy content
  useEffect(() => {
    const fetchLegalDocs = async () => {
      try {
        const [termsResponse, privacyResponse] = await Promise.all([
          fetch('/TERMS_OF_SERVICE.md'),
          fetch('/PRIVACY_POLICY.md')
        ]);

        if (termsResponse.ok) {
          const termsText = await termsResponse.text();
          setTermsContent(termsText);
        } else {
          setTermsContent('Failed to load Terms of Service.');
        }

        if (privacyResponse.ok) {
          const privacyText = await privacyResponse.text();
          setPrivacyContent(privacyText);
        } else {
          setPrivacyContent('Failed to load Privacy Policy.');
        }
      } catch (error) {
        console.error('Error loading legal documents:', error);
        setTermsContent('Error loading Terms of Service.');
        setPrivacyContent('Error loading Privacy Policy.');
      }
    };

    fetchLegalDocs();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate ToS acceptance for registration
    if (!isLogin && !acceptedTerms) {
      setError('You must accept the Terms of Service and Privacy Policy to register.');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        // Regular user login
        const response = await axios.post(`${API_URL}/login`, { username, password });
        // Extract CSRF token from response header (case-insensitive)
        const csrfToken = response.headers['x-csrf-token'] || response.headers['X-CSRF-Token'];
        login(response.data.user, csrfToken);

        // Check for extension redirect parameter
        const urlParams = new URLSearchParams(window.location.search);
        const extensionRedirect = urlParams.get('extension_redirect');

        if (extensionRedirect && isValidExtensionUrl(extensionRedirect)) {
          // Redirect to login success page with extension URL
          navigate(`/login-success?extension_url=${encodeURIComponent(extensionRedirect)}`);
        } else {
          // Normal web app navigation
          navigate('/');
        }
      } else {
        // Registration
        const response = await axios.post(`${API_URL}/register`, { username, email, password, accepted_terms: acceptedTerms });
        setSuccess(response.data.message || 'Registration successful! Please check your email to verify your account.');
        setUsername('');
        setEmail('');
        setPassword('');
        setAcceptedTerms(false);
        // Switch to login after 3 seconds to give time to read the message
        setTimeout(() => {
          setIsLogin(true);
          setSuccess('');
        }, 3000);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'An error occurred';
      setError(errorMessage);

      // Check if error is due to unverified email
      if (err.response?.status === 403 && err.response?.data?.email_verified === false) {
        setShowResendVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/resend-verification`, { email: resendEmail });
      setSuccess(response.data.message || 'Verification email sent! Please check your inbox.');
      setResendEmail('');
      setShowResendVerification(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resend verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMode = () => {
    setIsLogin(!isLogin);
    setError('');
    setSuccess('');
    setUsername('');
    setEmail('');
    setPassword('');
  };

  // Validate extension URL
  const isValidExtensionUrl = (url) => {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'chrome-extension:' || parsed.protocol === 'moz-extension:';
    } catch (e) {
      return false;
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/forgot-password`, { email: forgotPasswordEmail });
      setSuccess(response.data.message || 'Password reset email sent! Please check your inbox.');
      setForgotPasswordEmail('');
      setShowForgotPassword(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send password reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-logo">
          <h1>
            <img src={darkMode ? lavenderIconDark : lavenderIconLight} alt="Ref-Lex Icon" style={{ height: '3em', verticalAlign: 'bottom', marginRight: '0.3em' }} />
            <span className="logo-text">Ref-Lex</span>
          </h1>
          <p className="tagline">Another Reference Manager</p>
        </div>
        <h2>{isLogin ? 'Login' : 'Register'}</h2>
        {error && <div className="error">{error}</div>}
        {success && <div className="success">{success}</div>}
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={loading}
          />
          {!isLogin && (
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          )}
          <input
            type="password"
            placeholder="Password (12+ chars, uppercase, lowercase, number)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            minLength="12"
            title="Password must be at least 12 characters with uppercase, lowercase, and number"
          />
          {!isLogin && (
            <div className="legal-agreements">
              {/* Terms of Service Accordion */}
              <div className="agreement-section">
                <button
                  type="button"
                  className="agreement-header"
                  onClick={() => setExpandedTerms(!expandedTerms)}
                  disabled={loading}
                >
                  <span>Terms of Service</span>
                  <span className="collapse-icon">{expandedTerms ? '▼' : '▶'}</span>
                </button>
                {expandedTerms && (
                  <div className="agreement-content">
                    <pre>{termsContent}</pre>
                  </div>
                )}
              </div>

              {/* Privacy Policy Accordion */}
              <div className="agreement-section">
                <button
                  type="button"
                  className="agreement-header"
                  onClick={() => setExpandedPrivacy(!expandedPrivacy)}
                  disabled={loading}
                >
                  <span>Privacy Policy</span>
                  <span className="collapse-icon">{expandedPrivacy ? '▼' : '▶'}</span>
                </button>
                {expandedPrivacy && (
                  <div className="agreement-content">
                    <pre>{privacyContent}</pre>
                  </div>
                )}
              </div>

              {/* Checkbox */}
              <label className="agreement-checkbox">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  required
                  disabled={loading}
                />
                <span>I have read and agree to the Terms of Service and Privacy Policy above</span>
              </label>
            </div>
          )}
          <button type="submit" disabled={loading}>
            {loading ? (isLogin ? 'Logging in...' : 'Registering...') : (isLogin ? 'Login' : 'Register')}
          </button>
        </form>
        <p>
          {isLogin ? "Don't have an account? " : "Already have an account? "}
          <button onClick={handleToggleMode} className="link-button" disabled={loading}>
            {isLogin ? 'Register' : 'Login'}
          </button>
        </p>
        {isLogin && (
          <p style={{ marginTop: '15px', textAlign: 'center' }}>
            <button
              onClick={() => setShowForgotPassword(!showForgotPassword)}
              className="link-button"
              disabled={loading}
              style={{ fontSize: '14px' }}
            >
              {showForgotPassword ? 'Cancel' : 'Forgot Password?'}
            </button>
          </p>
        )}
        {showForgotPassword && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginTop: 0, fontSize: '16px' }}>Reset Password</h3>
            <form onSubmit={handleForgotPassword}>
              <input
                type="email"
                placeholder="Enter your email"
                value={forgotPasswordEmail}
                onChange={(e) => setForgotPasswordEmail(e.target.value)}
                required
                disabled={loading}
                style={{ marginBottom: '10px' }}
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </div>
        )}
        {showResendVerification && (
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: 'var(--card-bg)', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
            <h3 style={{ marginTop: 0, fontSize: '16px' }}>Resend Verification Email</h3>
            <form onSubmit={handleResendVerification}>
              <input
                type="email"
                placeholder="Enter your email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                required
                disabled={loading}
                style={{ marginBottom: '10px' }}
              />
              <button type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
